import { CatalogCategoryType } from '@/contexts/item-context-types';
import { FlashListProps } from '@shopify/flash-list';
import { useCallback } from 'react';
import { useCatalogGroupItemsStore } from '@/stores/catalog-group-items-store';
import { useCatalogLazyCarouselsEnabled } from './dynamic-config/use-catalog-lazy-carousels-enabled';
import { useDebouncedCallback } from 'use-debounce';
import { ViewToken } from 'react-native';

// The list also reports the header rows it injects, which have no category
interface CarouselRow {
  category?: CatalogCategoryType;
}

const REQUEST_DEBOUNCE_WAIT_MS = 250;
const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 10,
  minimumViewTime: 100,
};

export const useCatalogCarouselViewability = (): Pick<
  FlashListProps<unknown>,
  'onViewableItemsChanged' | 'viewabilityConfig'
> => {
  const lazyEnabled = useCatalogLazyCarouselsEnabled();

  const requestViewable = useDebouncedCallback((groupKeys: string[]) => {
    if (groupKeys.length) {
      useCatalogGroupItemsStore.getState().requestGroupItems(groupKeys);
    }
  }, REQUEST_DEBOUNCE_WAIT_MS);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken<CarouselRow>[] }) => {
      const groupKeys: string[] = [];
      for (const token of viewableItems) {
        const uuid = token.item.category?.uuid;
        if (token.isViewable && uuid) {
          groupKeys.push(uuid);
        }
      }

      requestViewable(groupKeys);
    },
    [requestViewable],
  );

  return lazyEnabled
    ? { onViewableItemsChanged, viewabilityConfig: VIEWABILITY_CONFIG }
    : {};
};
